// ============================================================================
// STRATEGY MONITOR / DEBUG CONSOLE — Real-time log viewer (v1.0)
// ============================================================================
// Temporary dev tool. Disable via REACT_APP_ENABLE_MONITOR_PAGE=false.
// ============================================================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Chip, Button, IconButton, TextField,
  Select, MenuItem, FormControl, InputLabel, Tooltip, Switch,
  FormControlLabel, Collapse, Alert, Badge, Divider,
} from '@mui/material';
import {
  Terminal as TerminalIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as ClearIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  FiberManualRecord as DotIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  BugReport as DebugIcon,
  CheckCircle as CheckIcon,
  Cancel as BlockIcon,
  TrendingUp as TrendIcon,
  ShowChart as ChartIcon,
  Shield as ShieldIcon,
  Speed as SpeedIcon,
  Layers as LayersIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import {
  getEvents, subscribe, clearEvents, connectSSE, disconnectSSE,
  getConnectionStatus, subscribeStatus, fetchRecentEvents, exportIssues,
} from '../services/MonitorEventStore';

// ── Level colors & icons ────────────────────────────────────────────────
const LEVEL_CONFIG = {
  error: { color: '#EF4444', bg: 'rgba(239,68,68,0.08)', icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
  warn:  { color: '#FBBF24', bg: 'rgba(245,158,11,0.08)', icon: <WarningIcon sx={{ fontSize: 14 }} /> },
  info:  { color: '#3b82f6', bg: 'rgba(96,165,250,0.06)', icon: <InfoIcon sx={{ fontSize: 14 }} /> },
  debug: { color: '#6B7280', bg: 'transparent', icon: <DebugIcon sx={{ fontSize: 14 }} /> },
};

const CATEGORY_COLORS = {
  scan: '#A855F7', gate: '#EF4444', entry: '#00FF94', position: '#3b82f6',
  exit: '#FBBF24', rescue: '#EF4444', risk: '#EF4444', network: '#3b82f6',
  data: '#00FF94', ui: '#F97316', console: '#9CA3AF',
};

// ── Strategy Group Header ────────────────────────────────────────────────
function StrategyGroupHeader({ strategyName, strategyId, eventCount, color }) {
  return (
    <Box sx={{
      px: 1.5, py: 0.8, display: 'flex', alignItems: 'center', gap: 1,
      bgcolor: 'rgba(168,85,247,0.04)', borderBottom: '1px solid rgba(168,85,247,0.15)',
      borderTop: '1px solid rgba(168,85,247,0.15)', position: 'sticky', top: 0, zIndex: 2,
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color || '#A855F7', boxShadow: `0 0 6px ${color || '#A855F7'}60` }} />
      <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#E5E7EB' }}>
        {strategyName || 'Unknown Strategy'}
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', color: '#6B7280', fontFamily: '"JetBrains Mono", monospace' }}>
        {strategyId?.slice(0, 8)}
      </Typography>
      <Chip label={`${eventCount} events`} size="small" sx={{
        height: 18, fontSize: '0.6rem', ml: 'auto',
        bgcolor: 'rgba(168,85,247,0.1)', color: '#A855F7', fontWeight: 600,
      }} />
    </Box>
  );
}

// ── Event Row ───────────────────────────────────────────────────────────
function EventRow({ event, isExpanded, onToggle, strategyName, showStrategyTag }) {
  const level = LEVEL_CONFIG[event.level] || LEVEL_CONFIG.debug;
  const catColor = CATEGORY_COLORS[event.category] || '#6B7280';
  const time = new Date(event.ts);
  const timeStr = time.toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const msStr = `.${String(time.getMilliseconds()).padStart(3, '0')}`;

  return (
    <Box
      onClick={onToggle}
      sx={{
        px: 1.5, py: 0.6, cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.03)',
        bgcolor: level.bg, '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
        fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontSize: '0.78rem',
        display: 'flex', alignItems: 'flex-start', gap: 1,
      }}
    >
      {/* Time */}
      <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', fontFamily: 'inherit', minWidth: 90, flexShrink: 0, pt: 0.1 }}>
        {timeStr}<span style={{ color: '#4B5563' }}>{msStr}</span>
      </Typography>

      {/* Level icon */}
      <Box sx={{ color: level.color, pt: 0.2, flexShrink: 0 }}>{level.icon}</Box>

      {/* Category */}
      <Chip
        label={event.category}
        size="small"
        sx={{
          height: 18, fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
          bgcolor: `${catColor}20`, color: catColor, border: `1px solid ${catColor}40`,
          flexShrink: 0,
        }}
      />

      {/* Strategy name tag */}
      {showStrategyTag && strategyName && (
        <Chip
          label={strategyName}
          size="small"
          sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: 'rgba(168,85,247,0.1)', color: '#A855F7', border: '1px solid rgba(168,85,247,0.2)', flexShrink: 0, maxWidth: 120, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
        />
      )}

      {/* Symbol */}
      {event.symbol && (
        <Chip
          label={event.symbol}
          size="small"
          sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.06)', color: '#D1D5DB', flexShrink: 0 }}
        />
      )}

      {/* Action */}
      <Typography sx={{
        color: level.color, fontWeight: 700, fontSize: '0.72rem', fontFamily: 'inherit',
        flexShrink: 0, minWidth: 0,
      }}>
        {event.action}
      </Typography>

      {/* Message */}
      <Typography sx={{
        color: '#D1D5DB', fontSize: '0.72rem', fontFamily: 'inherit',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap',
        flex: 1, minWidth: 0,
      }}>
        {event.message}
      </Typography>
    </Box>
  );
}

// ── Scan Cycle Detail — Rich visual display for SCAN_CYCLE_DETAIL events ──
function ScanCycleDetail({ data }) {
  const [showAllTools, setShowAllTools] = React.useState(false);
  const [showRejections, setShowRejections] = React.useState(false);
  const [showSignals, setShowSignals] = React.useState(false);

  if (!data) return null;

  const ctx = data.marketContext;
  const tools = data.tools || [];
  const enabledTools = tools.filter(t => t.enabled);
  const disabledTools = tools.filter(t => !t.enabled);
  const displayTools = showAllTools ? tools : enabledTools;
  const rejections = data.rejections || [];
  const filterSteps = data.filterSteps || [];
  const gateBreakdown = data.gateBreakdown || {};
  const topSignals = data.topSignals || [];

  // Color for regime
  const regimeColor = ctx?.regime === 'strong_trend' ? '#00FF94' : ctx?.regime === 'weak_trend' ? '#3b82f6'
    : ctx?.regime === 'ranging' ? '#FBBF24' : ctx?.regime === 'choppy' ? '#EF4444'
    : ctx?.regime === 'volatile_spike' ? '#F97316' : '#6B7280';

  // Color for HTF bias
  const biasColor = ctx?.htfBias === 'bullish' ? '#00FF94' : ctx?.htfBias === 'bearish' ? '#EF4444' : '#6B7280';

  return (
    <Box sx={{
      px: 2, py: 1.5, bgcolor: 'rgba(0,0,0,0.4)', borderBottom: '1px solid rgba(255,255,255,0.06)',
      fontFamily: '"Inter", sans-serif', fontSize: '0.75rem',
    }}>
      {/* ── Header: Strategy + Mode ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Chip label={data.modeId} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(168,85,247,0.15)', color: '#A855F7', border: '1px solid rgba(168,85,247,0.3)' }} />
        <Typography sx={{ color: '#9CA3AF', fontSize: '0.7rem' }}>
          {data.symbolsScanned}/{data.symbolsTotal} symbols in {(data.scanDurationMs / 1000).toFixed(1)}s
          {data.scanBudgetExceeded && <span style={{ color: '#FBBF24' }}> (budget exceeded)</span>}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          <Chip label={`${data.signalsFound} signals`} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(59,130,246,0.15)', color: '#3b82f6' }} />
          <Chip label={`${data.qualifiedCount} qualified`} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(251,191,36,0.15)', color: '#FBBF24' }} />
          <Chip label={`${data.entriesMade} entries`} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: data.entriesMade > 0 ? 'rgba(0,255,148,0.15)' : 'rgba(239,68,68,0.1)', color: data.entriesMade > 0 ? '#00FF94' : '#EF4444' }} />
        </Box>
      </Box>

      {/* ── Market Context ── */}
      {ctx && (
        <Box sx={{ mb: 1.5, p: 1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.65rem', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Market Context</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ChartIcon sx={{ fontSize: 14, color: regimeColor }} />
              <Typography sx={{ color: regimeColor, fontSize: '0.72rem', fontWeight: 700 }}>{ctx.regime}</Typography>
              <Typography sx={{ color: '#6B7280', fontSize: '0.68rem' }}>ADX {ctx.adx?.toFixed(0)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendIcon sx={{ fontSize: 14, color: biasColor }} />
              <Typography sx={{ color: biasColor, fontSize: '0.72rem', fontWeight: 600 }}>HTF: {ctx.htfBias}</Typography>
              <Typography sx={{ color: '#6B7280', fontSize: '0.68rem' }}>str {ctx.htfTrendStrength?.toFixed(0)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LayersIcon sx={{ fontSize: 14, color: '#A855F7' }} />
              <Typography sx={{ color: '#D1D5DB', fontSize: '0.72rem', fontWeight: 600 }}>{ctx.loadout}</Typography>
            </Box>
            {ctx.nearestSupport && (
              <Typography sx={{ color: '#6B7280', fontSize: '0.65rem' }}>S: ${ctx.nearestSupport?.toFixed(2)}</Typography>
            )}
            {ctx.nearestResistance && (
              <Typography sx={{ color: '#6B7280', fontSize: '0.65rem' }}>R: ${ctx.nearestResistance?.toFixed(2)}</Typography>
            )}
          </Box>
          <Typography sx={{ color: '#6B7280', fontSize: '0.65rem', mt: 0.5, fontStyle: 'italic' }}>{ctx.loadoutDescription}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            {(ctx.requiredGates || []).map(g => (
              <Chip key={g} label={g} size="small" sx={{ height: 18, fontSize: '0.58rem', bgcolor: 'rgba(239,68,68,0.1)', color: '#EF4444', fontWeight: 600 }} />
            ))}
            {(ctx.optionalGates || []).map(g => (
              <Chip key={g} label={g} size="small" sx={{ height: 18, fontSize: '0.58rem', bgcolor: 'rgba(107,114,128,0.15)', color: '#6B7280' }} />
            ))}
            <Typography sx={{ color: '#6B7280', fontSize: '0.6rem', ml: 0.5 }}>
              floor: {ctx.confidenceFloor}% | max: {ctx.maxEntries} entries | sells: {ctx.allowSells ? '✓' : '✗'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Confidence Threshold ── */}
      <Box sx={{ mb: 1.5, p: 1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <Typography sx={{ color: '#9CA3AF', fontSize: '0.65rem', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Confidence Pipeline</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ color: '#6B7280', fontSize: '0.6rem' }}>Base</Typography>
            <Typography sx={{ color: '#D1D5DB', fontSize: '0.8rem', fontWeight: 700 }}>{data.baseThreshold}%</Typography>
          </Box>
          {data.leverageBoost > 0 && (
            <>
              <Typography sx={{ color: '#4B5563', fontSize: '0.8rem' }}>+</Typography>
              <Box>
                <Typography sx={{ color: '#6B7280', fontSize: '0.6rem' }}>Leverage</Typography>
                <Typography sx={{ color: '#FBBF24', fontSize: '0.8rem', fontWeight: 700 }}>+{data.leverageBoost?.toFixed(1)}%</Typography>
              </Box>
            </>
          )}
          <Typography sx={{ color: '#4B5563', fontSize: '0.8rem' }}>=</Typography>
          <Box>
            <Typography sx={{ color: '#6B7280', fontSize: '0.6rem' }}>Effective</Typography>
            <Typography sx={{ color: '#00FF94', fontSize: '0.8rem', fontWeight: 700 }}>{data.threshold?.toFixed(1)}%</Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Tools & Indicators ── */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Tools & Indicators ({enabledTools.length} active / {tools.length} total)
          </Typography>
          <Button size="small" onClick={() => setShowAllTools(!showAllTools)} sx={{ fontSize: '0.6rem', color: '#6B7280', minWidth: 0, py: 0 }}>
            {showAllTools ? 'Show active only' : 'Show all'}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {displayTools.map((tool, i) => (
            <Tooltip key={i} title={
              <Box sx={{ fontSize: '0.7rem' }}>
                <strong>{tool.name}</strong>
                {tool.params && Object.entries(tool.params).map(([k, v]) => (
                  <Box key={k}>{k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}</Box>
                ))}
                {tool.result && <Box sx={{ mt: 0.5, color: '#A855F7' }}>Result: {tool.result}</Box>}
              </Box>
            } arrow placement="top">
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.3,
                px: 0.8, py: 0.3, borderRadius: 1,
                bgcolor: tool.enabled ? 'rgba(0,255,148,0.06)' : 'rgba(107,114,128,0.06)',
                border: `1px solid ${tool.enabled ? 'rgba(0,255,148,0.15)' : 'rgba(107,114,128,0.15)'}`,
                cursor: 'default',
              }}>
                {tool.enabled
                  ? <CheckIcon sx={{ fontSize: 11, color: '#00FF94' }} />
                  : <BlockIcon sx={{ fontSize: 11, color: '#4B5563' }} />
                }
                <Typography sx={{
                  fontSize: '0.62rem', fontWeight: 600,
                  color: tool.enabled ? '#D1D5DB' : '#4B5563',
                  textDecoration: tool.enabled ? 'none' : 'line-through',
                }}>
                  {tool.name}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {/* ── Signal Matrix (if enabled) ── */}
      {data.signalMatrix?.enabled && (
        <Box sx={{ mb: 1.5, p: 1, borderRadius: 1, bgcolor: 'rgba(168,85,247,0.04)', border: '1px solid rgba(168,85,247,0.15)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <PsychologyIcon sx={{ fontSize: 14, color: '#A855F7' }} />
            <Typography sx={{ color: '#A855F7', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Signal Matrix</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(data.signalMatrix.indicators || []).map((ind, i) => (
              <Box key={i} sx={{
                px: 0.8, py: 0.3, borderRadius: 1,
                bgcolor: ind.enabled ? 'rgba(168,85,247,0.08)' : 'rgba(107,114,128,0.06)',
                border: `1px solid ${ind.enabled ? 'rgba(168,85,247,0.2)' : 'rgba(107,114,128,0.1)'}`,
              }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: ind.enabled ? '#D1D5DB' : '#4B5563' }}>
                  {ind.name} <span style={{ color: '#A855F7' }}>w:{ind.weight}</span>
                </Typography>
                <Typography sx={{ fontSize: '0.55rem', color: '#6B7280' }}>
                  {(ind.timeframes || []).join(', ')}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Scalp Gate ── */}
      {data.scalpGate?.active && (
        <Box sx={{ mb: 1.5, p: 0.8, borderRadius: 1, bgcolor: 'rgba(249,115,22,0.04)', border: '1px solid rgba(249,115,22,0.15)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SpeedIcon sx={{ fontSize: 14, color: '#F97316' }} />
            <Typography sx={{ color: '#F97316', fontSize: '0.65rem', fontWeight: 600 }}>Scalp Gate: {data.scalpGate.profile}</Typography>
            {data.scalpGate.skippedGates?.length > 0 && (
              <Typography sx={{ color: '#6B7280', fontSize: '0.6rem' }}>
                skipped: [{data.scalpGate.skippedGates.join(', ')}]
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* ── Filter Steps (funnel) ── */}
      {filterSteps.length > 0 && (
        <Box sx={{ mb: 1.5, p: 1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.65rem', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filter Funnel</Typography>
          {filterSteps.map((fs, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.2 }}>
              <ShieldIcon sx={{ fontSize: 12, color: '#EF4444' }} />
              <Typography sx={{ color: '#D1D5DB', fontSize: '0.68rem', fontWeight: 600, minWidth: 180 }}>{fs.step}</Typography>
              <Typography sx={{ color: '#EF4444', fontSize: '0.68rem', fontWeight: 700 }}>{fs.before} → {fs.after}</Typography>
              <Typography sx={{ color: '#6B7280', fontSize: '0.65rem' }}>(-{fs.dropped})</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ── Gate Breakdown (rejections by gate) ── */}
      {Object.keys(gateBreakdown).length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Box
            onClick={() => setShowRejections(!showRejections)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: 0.5, '&:hover': { opacity: 0.8 } }}
          >
            <ShieldIcon sx={{ fontSize: 14, color: '#EF4444' }} />
            <Typography sx={{ color: '#EF4444', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Gate Rejections ({rejections.length})
            </Typography>
            {showRejections ? <CollapseIcon sx={{ fontSize: 14, color: '#6B7280' }} /> : <ExpandIcon sx={{ fontSize: 14, color: '#6B7280' }} />}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: showRejections ? 1 : 0 }}>
            {Object.entries(gateBreakdown)
              .sort(([, a], [, b]) => b - a)
              .map(([gate, count]) => (
                <Chip key={gate} label={`${gate}: ${count}`} size="small" sx={{
                  height: 20, fontSize: '0.6rem', fontWeight: 700,
                  bgcolor: 'rgba(239,68,68,0.1)', color: '#EF4444',
                  border: '1px solid rgba(239,68,68,0.2)',
                }} />
              ))}
          </Box>
          <Collapse in={showRejections}>
            <Box sx={{ maxHeight: 200, overflow: 'auto', pl: 1 }}>
              {rejections.map((r, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, py: 0.2, borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                  <Typography sx={{ color: '#FBBF24', fontSize: '0.62rem', fontWeight: 600, minWidth: 100 }}>{r.symbol}</Typography>
                  <Chip label={r.gate} size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: 'rgba(239,68,68,0.1)', color: '#EF4444' }} />
                  <Typography sx={{ color: '#6B7280', fontSize: '0.62rem', flex: 1 }}>{r.reason}</Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* ── Top Signals ── */}
      {topSignals.length > 0 && (
        <Box>
          <Box
            onClick={() => setShowSignals(!showSignals)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mb: 0.5, '&:hover': { opacity: 0.8 } }}
          >
            <TrendIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
            <Typography sx={{ color: '#3b82f6', fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Top Signals ({topSignals.length})
            </Typography>
            {showSignals ? <CollapseIcon sx={{ fontSize: 14, color: '#6B7280' }} /> : <ExpandIcon sx={{ fontSize: 14, color: '#6B7280' }} />}
          </Box>
          <Collapse in={showSignals}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {topSignals.map((s, i) => (
                <Box key={i} sx={{
                  px: 0.8, py: 0.3, borderRadius: 1,
                  bgcolor: s.side === 'buy' ? 'rgba(0,255,148,0.06)' : 'rgba(239,68,68,0.06)',
                  border: `1px solid ${s.side === 'buy' ? 'rgba(0,255,148,0.15)' : 'rgba(239,68,68,0.15)'}`,
                }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: s.side === 'buy' ? '#00FF94' : '#EF4444' }}>
                    {s.side?.toUpperCase()} {s.symbol}
                  </Typography>
                  <Typography sx={{ fontSize: '0.55rem', color: '#6B7280' }}>
                    {s.confidence?.toFixed(1)}% ({s.method})
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

// ── Expanded Detail ─────────────────────────────────────────────────────
function EventDetail({ event }) {
  // Use rich ScanCycleDetail for SCAN_CYCLE_DETAIL events
  if (event.action === 'SCAN_CYCLE_DETAIL' && event.data) {
    return <ScanCycleDetail data={event.data} />;
  }

  if (!event.data && !event.strategyId && !event.runId && !event.positionId) return null;

  return (
    <Box sx={{
      px: 2, py: 1, bgcolor: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.05)',
      fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontSize: '0.7rem',
    }}>
      {event.strategyId && <Typography sx={{ color: '#6B7280', fontSize: '0.68rem' }}>Strategy: {event.strategyId}</Typography>}
      {event.runId && <Typography sx={{ color: '#6B7280', fontSize: '0.68rem' }}>Run: {event.runId}</Typography>}
      {event.positionId && <Typography sx={{ color: '#6B7280', fontSize: '0.68rem' }}>Position: {event.positionId}</Typography>}
      {event.fingerprint && <Typography sx={{ color: '#4B5563', fontSize: '0.65rem' }}>FP: {event.fingerprint}</Typography>}
      {event.data && (
        <Box sx={{ mt: 0.5 }}>
          {event.data.stack ? (
            <Box sx={{ color: '#EF4444', whiteSpace: 'pre-wrap', fontSize: '0.65rem', maxHeight: 200, overflow: 'auto' }}>
              {event.data.stack}
            </Box>
          ) : (
            <Box sx={{ color: '#9CA3AF', whiteSpace: 'pre-wrap', fontSize: '0.65rem', maxHeight: 200, overflow: 'auto' }}>
              {JSON.stringify(event.data, null, 2)}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Error Summary Box ───────────────────────────────────────────────────
function ErrorSummary({ events, onFilterFingerprint }) {
  const errorGroups = useMemo(() => {
    const groups = {};
    events.filter(e => e.level === 'error' || e.level === 'warn').forEach(e => {
      const fp = e.fingerprint || e.action;
      if (!groups[fp]) groups[fp] = { action: e.action, message: e.message, level: e.level, count: 0, latest: 0 };
      groups[fp].count++;
      groups[fp].latest = Math.max(groups[fp].latest, e.ts);
    });
    return Object.entries(groups)
      .sort(([, a], [, b]) => b.latest - a.latest)
      .slice(0, 10);
  }, [events]);

  if (errorGroups.length === 0) return null;

  const totalErrors = errorGroups.reduce((sum, [, g]) => sum + (g.level === 'error' ? g.count : 0), 0);
  const totalWarns = errorGroups.reduce((sum, [, g]) => sum + (g.level === 'warn' ? g.count : 0), 0);

  return (
    <Paper sx={{ mb: 1.5, p: 1.5, border: '1px solid rgba(239,68,68,0.2)', bgcolor: 'rgba(239,68,68,0.04)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ErrorIcon sx={{ color: '#EF4444', fontSize: 18 }} />
        <Typography sx={{ color: '#EF4444', fontWeight: 700, fontSize: '0.85rem' }}>
          Issues ({totalErrors} errors, {totalWarns} warnings)
        </Typography>
      </Box>
      {errorGroups.map(([fp, group]) => (
        <Box
          key={fp}
          onClick={() => onFilterFingerprint(fp)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.4,
            cursor: 'pointer', borderRadius: 1,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
          }}
        >
          <Chip
            label={group.count}
            size="small"
            sx={{
              height: 20, minWidth: 28, fontSize: '0.7rem', fontWeight: 700,
              bgcolor: group.level === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
              color: group.level === 'error' ? '#EF4444' : '#FBBF24',
            }}
          />
          <Typography sx={{ color: '#D1D5DB', fontSize: '0.75rem', fontWeight: 600 }}>{group.action}</Typography>
          <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {group.message?.substring(0, 80)}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

// ── Main Monitor Page ───────────────────────────────────────────────────
export default function Monitor() {
  const [allEvents, setAllEvents] = useState(() => getEvents());
  const [connectionStatus, setConnectionStatus] = useState(getConnectionStatus());
  const [paused, setPaused] = useState(false);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [fingerprintFilter, setFingerprintFilter] = useState(null);
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [strategies, setStrategies] = useState([]);
  const [groupByStrategy, setGroupByStrategy] = useState(true);
  const listRef = useRef(null);
  const autoScrollRef = useRef(true);

  // Load strategies
  useEffect(() => {
    const STRATEGY_ENGINE_URL = process.env.REACT_APP_STRATEGY_ENGINE_URL || 'https://strategy-engine.houman-ghavamzadeh.workers.dev';
    const token = localStorage.getItem('auth_token');
    fetch(`${STRATEGY_ENGINE_URL}/api/strategies`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.data || []);
        setStrategies(list);
      }).catch(() => {});
  }, []);

  // Strategy name lookup map
  const strategyMap = useMemo(() => {
    const map = {};
    strategies.forEach(s => { map[s.id] = s.name || s.strategyName || s.id?.slice(0, 8); });
    return map;
  }, [strategies]);

  // Strategy color palette for group headers
  const STRATEGY_COLORS = ['#A855F7', '#3b82f6', '#00FF94', '#FBBF24', '#F97316', '#EF4444', '#EC4899', '#06B6D4'];
  const strategyColorMap = useMemo(() => {
    const map = {};
    strategies.forEach((s, i) => { map[s.id] = STRATEGY_COLORS[i % STRATEGY_COLORS.length]; });
    return map;
  }, [strategies]);

  // Connect SSE on mount
  useEffect(() => {
    connectSSE(strategyFilter !== 'all' ? strategyFilter : null);
    fetchRecentEvents(strategyFilter !== 'all' ? strategyFilter : null);
    return () => disconnectSSE();
  }, [strategyFilter]);

  // Subscribe to events
  useEffect(() => {
    const unsub = subscribe(() => {
      if (!paused) setAllEvents([...getEvents()]);
    });
    const unsubStatus = subscribeStatus(setConnectionStatus);
    return () => { unsub(); unsubStatus(); };
  }, [paused]);

  // Auto-scroll
  useEffect(() => {
    if (autoScrollRef.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [allEvents]);

  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 50;
  }, []);

  // Filtered events
  const filteredEvents = useMemo(() => {
    let result = allEvents;
    if (errorsOnly) result = result.filter(e => e.level === 'error' || e.level === 'warn');
    if (levelFilter !== 'all') result = result.filter(e => e.level === levelFilter);
    if (categoryFilter !== 'all') result = result.filter(e => e.category === categoryFilter);
    if (sourceFilter !== 'all') result = result.filter(e => e.source === sourceFilter);
    if (fingerprintFilter) result = result.filter(e => e.fingerprint === fingerprintFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(e =>
        e.message?.toLowerCase().includes(q) ||
        e.action?.toLowerCase().includes(q) ||
        e.symbol?.toLowerCase().includes(q) ||
        e.category?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [allEvents, errorsOnly, levelFilter, categoryFilter, sourceFilter, search, fingerprintFilter]);

  const handleCopyIssues = useCallback(() => {
    const report = exportIssues(filteredEvents, { strategyId: strategyFilter !== 'all' ? strategyFilter : undefined });
    navigator.clipboard.writeText(report).then(() => {
      // Success feedback could be a toast — for now just log
    }).catch(() => {});
  }, [filteredEvents, strategyFilter]);

  const handleClearFilters = () => {
    setSearch('');
    setLevelFilter('all');
    setCategoryFilter('all');
    setSourceFilter('all');
    setErrorsOnly(false);
    setFingerprintFilter(null);
  };

  const statusColor = connectionStatus === 'connected' ? '#00FF94' : connectionStatus === 'connecting' ? '#FBBF24' : '#EF4444';

  const errorCount = allEvents.filter(e => e.level === 'error').length;
  const warnCount = allEvents.filter(e => e.level === 'warn').length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)', minHeight: 400 }}>
      {/* Header */}
      <Box sx={{
        p: 2, borderRadius: 2, mb: 1.5,
        background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TerminalIcon sx={{ fontSize: 28, color: '#00FF94' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
            Strategy Monitor
          </Typography>
          <Tooltip title={connectionStatus}>
            <DotIcon sx={{ fontSize: 14, color: statusColor, ml: 0.5 }} />
          </Tooltip>
          <Typography sx={{ color: statusColor, fontSize: '0.72rem', fontWeight: 600 }}>
            {connectionStatus}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
          {errorCount > 0 && (
            <Chip
              icon={<ErrorIcon sx={{ fontSize: 14 }} />}
              label={`${errorCount} errors`}
              size="small"
              sx={{ bgcolor: 'rgba(239,68,68,0.15)', color: '#EF4444', fontWeight: 700, fontSize: '0.72rem', height: 26 }}
              onClick={() => { setErrorsOnly(true); setLevelFilter('error'); }}
            />
          )}
          {warnCount > 0 && (
            <Chip
              icon={<WarningIcon sx={{ fontSize: 14 }} />}
              label={`${warnCount} warns`}
              size="small"
              sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#FBBF24', fontWeight: 700, fontSize: '0.72rem', height: 26 }}
              onClick={() => { setErrorsOnly(true); setLevelFilter('warn'); }}
            />
          )}

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={strategyFilter}
              onChange={(e) => setStrategyFilter(e.target.value)}
              sx={{ fontSize: '0.78rem', height: 32 }}
            >
              <MenuItem value="all">All Strategies</MenuItem>
              {strategies.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title={groupByStrategy ? 'Timeline view' : 'Group by strategy'}>
            <IconButton size="small" onClick={() => setGroupByStrategy(!groupByStrategy)} sx={{ color: groupByStrategy ? '#A855F7' : '#9CA3AF' }}>
              <LayersIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={paused ? 'Resume stream' : 'Pause stream'}>
            <IconButton size="small" onClick={() => setPaused(!paused)} sx={{ color: paused ? '#FBBF24' : '#9CA3AF' }}>
              {paused ? <PlayIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Toggle filters">
            <IconButton size="small" onClick={() => setShowFilters(!showFilters)} sx={{ color: showFilters ? '#00FF94' : '#9CA3AF' }}>
              <FilterIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Clear view">
            <IconButton size="small" onClick={() => { clearEvents(); setAllEvents([]); }} sx={{ color: '#9CA3AF' }}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Copy all issues to clipboard">
            <IconButton size="small" onClick={handleCopyIssues} sx={{ color: '#9CA3AF', '&:hover': { color: '#00FF94' } }}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Reconnect">
            <IconButton size="small" onClick={() => connectSSE(strategyFilter !== 'all' ? strategyFilter : null)} sx={{ color: '#9CA3AF' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 1.5, mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search messages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, mr: 0.5, color: '#6B7280' }} /> }}
            sx={{ width: 200, '& input': { fontSize: '0.78rem' } }}
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Level</InputLabel>
            <Select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} label="Level" sx={{ fontSize: '0.78rem', height: 32 }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warn">Warn</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="debug">Debug</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Category</InputLabel>
            <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} label="Category" sx={{ fontSize: '0.78rem', height: 32 }}>
              <MenuItem value="all">All</MenuItem>
              {['scan','gate','entry','position','exit','rescue','risk','network','data','ui','console'].map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Source</InputLabel>
            <Select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} label="Source" sx={{ fontSize: '0.78rem', height: 32 }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="backend">Backend</MenuItem>
              <MenuItem value="frontend">Frontend</MenuItem>
              <MenuItem value="broker">Broker</MenuItem>
              <MenuItem value="system">System</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch size="small" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />}
            label={<Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>Errors only</Typography>}
          />
          {(search || levelFilter !== 'all' || categoryFilter !== 'all' || sourceFilter !== 'all' || errorsOnly || fingerprintFilter) && (
            <Button size="small" onClick={handleClearFilters} sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>
              Clear filters
            </Button>
          )}
          {fingerprintFilter && (
            <Chip
              label={`FP: ${fingerprintFilter}`}
              size="small"
              onDelete={() => setFingerprintFilter(null)}
              sx={{ bgcolor: 'rgba(239,68,68,0.1)', color: '#EF4444', fontSize: '0.68rem' }}
            />
          )}
          <Typography sx={{ ml: 'auto', color: '#6B7280', fontSize: '0.72rem' }}>
            {filteredEvents.length} / {allEvents.length} events
          </Typography>
        </Paper>
      </Collapse>

      {/* Error Summary */}
      <ErrorSummary events={allEvents} onFilterFingerprint={setFingerprintFilter} />

      {/* Event List */}
      <Paper
        ref={listRef}
        onScroll={handleScroll}
        sx={{
          flex: 1, overflow: 'auto', borderRadius: 2,
          bgcolor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 3 },
        }}
      >
        {filteredEvents.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <TerminalIcon sx={{ fontSize: 48, color: '#374151', mb: 1 }} />
            <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>
              {allEvents.length === 0 ? 'Waiting for events...' : 'No events match filters'}
            </Typography>
            {paused && (
              <Typography sx={{ color: '#FBBF24', fontSize: '0.78rem', mt: 0.5 }}>
                Stream is paused
              </Typography>
            )}
          </Box>
        ) : groupByStrategy && strategyFilter === 'all' ? (
          // Grouped view: group events by strategy with headers
          (() => {
            const eventsToShow = filteredEvents.slice(-1000);
            // Group events by strategyId
            const groups = {};
            const noStrategyEvents = [];
            eventsToShow.forEach(e => {
              const sid = e.strategyId || e.data?.strategyId;
              if (sid) {
                if (!groups[sid]) groups[sid] = [];
                groups[sid].push(e);
              } else {
                noStrategyEvents.push(e);
              }
            });
            // Sort groups by most recent event
            const sortedGroups = Object.entries(groups).sort(([, a], [, b]) => {
              const latestA = Math.max(...a.map(e => e.ts || 0));
              const latestB = Math.max(...b.map(e => e.ts || 0));
              return latestB - latestA;
            });

            return (
              <>
                {sortedGroups.map(([sid, events]) => (
                  <React.Fragment key={`group-${sid}`}>
                    <StrategyGroupHeader
                      strategyName={strategyMap[sid] || events[0]?.data?.strategyName}
                      strategyId={sid}
                      eventCount={events.length}
                      color={strategyColorMap[sid]}
                    />
                    {events.map(event => (
                      <React.Fragment key={event.id}>
                        <EventRow
                          event={event}
                          isExpanded={expandedId === event.id}
                          onToggle={() => setExpandedId(expandedId === event.id ? null : event.id)}
                          strategyName={strategyMap[sid]}
                          showStrategyTag={false}
                        />
                        {expandedId === event.id && <EventDetail event={event} />}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
                {noStrategyEvents.length > 0 && (
                  <React.Fragment>
                    <StrategyGroupHeader
                      strategyName="System Events"
                      strategyId=""
                      eventCount={noStrategyEvents.length}
                      color="#6B7280"
                    />
                    {noStrategyEvents.map(event => (
                      <React.Fragment key={event.id}>
                        <EventRow
                          event={event}
                          isExpanded={expandedId === event.id}
                          onToggle={() => setExpandedId(expandedId === event.id ? null : event.id)}
                          showStrategyTag={false}
                        />
                        {expandedId === event.id && <EventDetail event={event} />}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                )}
              </>
            );
          })()
        ) : (
          // Flat view: show strategy name tag on each event
          filteredEvents.slice(-1000).map(event => {
            const sid = event.strategyId || event.data?.strategyId;
            return (
              <React.Fragment key={event.id}>
                <EventRow
                  event={event}
                  isExpanded={expandedId === event.id}
                  onToggle={() => setExpandedId(expandedId === event.id ? null : event.id)}
                  strategyName={sid ? strategyMap[sid] : null}
                  showStrategyTag={strategyFilter === 'all'}
                />
                {expandedId === event.id && <EventDetail event={event} />}
              </React.Fragment>
            );
          })
        )}
      </Paper>

      {/* Footer status */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, px: 1 }}>
        <Typography sx={{ color: '#4B5563', fontSize: '0.68rem' }}>
          Buffer: {allEvents.length}/5000 | Showing: {Math.min(filteredEvents.length, 1000)}
        </Typography>
        {paused && (
          <Chip label="PAUSED" size="small" sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#FBBF24', height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
        )}
      </Box>
    </Box>
  );
}
